import React from 'react';
import ErrorBoundary from './ErrorBoundary';
import { AlertTriangle, RefreshCw, Download, Bug } from 'lucide-react';

interface WhiteboardErrorBoundaryProps {
  children: React.ReactNode;
  onReset?: () => void;
}

export const WhiteboardErrorBoundary: React.FC<WhiteboardErrorBoundaryProps> = ({ 
  children, 
  onReset 
}) => {
  const whiteboardErrorFallback = (error: Error, errorInfo: React.ErrorInfo, reset: () => void) => {
    const handleReset = () => {
      onReset?.();
      reset();
    };

    const handleDownloadErrorLog = () => {
      const errorData = {
        timestamp: new Date().toISOString(),
        error: {
          message: error.message,
          stack: error.stack
        },
        errorInfo: errorInfo,
        userAgent: navigator.userAgent,
        url: window.location.href
      };

      const blob = new Blob([JSON.stringify(errorData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `whiteboard-error-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    };

    const isCanvasError = error.message.toLowerCase().includes('canvas') || 
                         error.message.toLowerCase().includes('fabric');
    const isMemoryError = error.message.toLowerCase().includes('memory') ||
                          error.message.toLowerCase().includes('maximum call stack');

    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900 p-4">
        <div className="max-w-lg w-full bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6">
          <div className="flex items-center justify-center w-16 h-16 mx-auto bg-orange-100 dark:bg-orange-900 rounded-full">
            <AlertTriangle className="w-8 h-8 text-orange-600 dark:text-orange-400" />
          </div>

          <h2 className="mt-4 text-2xl font-bold text-center text-gray-900 dark:text-gray-100">
            Whiteboard Hatası
          </h2>

          <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            {isCanvasError && (
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Canvas ile ilgili bir hata oluştu. Bu genellikle tarayıcı uyumluluk sorunlarından kaynaklanır.
              </p>
            )}
            {isMemoryError && (
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Bellek sınırına ulaşıldı. Lütfen sayfayı yenileyerek yeniden başlayın.
              </p>
            )}
            {!isCanvasError && !isMemoryError && (
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Whiteboard yüklenirken beklenmeyen bir hata oluştu.
              </p>
            )}
          </div>

          <div className="mt-6 space-y-3">
            <button
              onClick={handleReset}
              className="w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              <RefreshCw className="w-5 h-5 mr-2" />
              Whiteboard'u Yeniden Başlat
            </button>

            <button
              onClick={() => window.location.reload()}
              className="w-full flex items-center justify-center px-4 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium"
            >
              <RefreshCw className="w-5 h-5 mr-2" />
              Sayfayı Yenile
            </button>

            {process.env.NODE_ENV === 'development' && (
              <button
                onClick={handleDownloadErrorLog}
                className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm"
              >
                <Download className="w-4 h-4 mr-2" />
                Hata Logunu İndir
              </button>
            )}
          </div>

          {process.env.NODE_ENV === 'development' && (
            <details className="mt-6">
              <summary className="cursor-pointer text-sm font-medium text-gray-600 dark:text-gray-400 flex items-center">
                <Bug className="w-4 h-4 mr-1" />
                Hata Detayları
              </summary>
              <div className="mt-2 p-3 bg-gray-100 dark:bg-gray-700 rounded text-xs font-mono overflow-auto max-h-40">
                <p className="text-red-600 dark:text-red-400 font-semibold">{error.message}</p>
                <pre className="mt-2 text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                  {error.stack}
                </pre>
              </div>
            </details>
          )}

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500 dark:text-gray-500">
              Sorun devam ederse, lütfen bu sayfanın ekran görüntüsünü alarak destek ekibine iletin.
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <ErrorBoundary fallback={whiteboardErrorFallback}>
      {children}
    </ErrorBoundary>
  );
};